import {
  Chip,
  createFilterOptions,
  Grid,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import { InputControl } from "../../../shared";

function BasicDetailsSection({
  ingredient,
  validator,
  onChange,
  onChangeTax,
  optionsTaxGroup,
  optionsTypes,
  optionsTags,
  handleProductInputChange,
  onDeleteTagSelect,
}) {
  const filter = createFilterOptions();
  const isAlternateNameRequired =
    ingredient?.data?.stocktake_display_name === "alternate_name";

  return (
    <Grid
      container
      gap="24px"
      py="24px"
      borderBottom="1px solid"
      borderColor="divider"
    >
      <Grid item xs={12} sm={4}>
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{ width: "150px" }}
        >
          Basic Details
        </Typography>
      </Grid>
      <Grid item flex="1">
        <Stack width="100%" gap="16px">
          <InputControl
            name="name"
            type="text"
            label="Ingredient Name"
            required
            error={validator.current.message(
              "Ingredient Name",
              ingredient?.data?.name,
              "required",
            )}
            helperText={validator.current.message(
              "Ingredient Name",
              ingredient?.data?.name,
              "required",
            )}
            onChange={onChange}
            value={ingredient?.data?.name}
          />
          <InputControl
            name="alternate_name"
            type="text"
            label="Alternate Name"
            value={ingredient?.data?.alternate_name}
            onChange={onChange}
            required={isAlternateNameRequired}
            error={
              isAlternateNameRequired
                ? validator.current.message(
                    "Alternate Name",
                    ingredient?.data?.alternate_name,
                    "required",
                  )
                : false
            }
            helperText={
              isAlternateNameRequired
                ? validator.current.message(
                    "Alternate Name",
                    ingredient?.data?.alternate_name,
                    "required",
                  )
                : null
            }
          />

          <Stack direction="row" gap="16px">
            {/* Tax Group Dropdown */}
            <InputControl
              label="Tax Group"
              sx={{ width: "100%" }}
              type="dropdown"
              disableClearable
              required
              options={optionsTaxGroup ?? []}
              getOptionLabel={(option) => option?.label}
              value={
                (optionsTaxGroup?.length > 0 &&
                  ingredient?.data?.tax_group_id &&
                  optionsTaxGroup?.find(
                    (c) => c.value === ingredient?.data?.tax_group_id,
                  )) ||
                null
              }
              onChange={(e, newValue) => {
                onChangeTax({
                  target: {
                    name: "tax_group_id",
                    value: newValue?.value,
                  },
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tax Group"
                  required
                  error={validator.current.message(
                    "Tax Group",
                    ingredient?.data?.tax_group_id,
                    "required",
                  )}
                  helperText={validator.current.message(
                    "Tax Group",
                    ingredient?.data?.tax_group_id,
                    "required",
                  )}
                />
              )}
            />

            {/* Type Dropdown */}
            <InputControl
              label="Type"
              required
              sx={{ width: "100%" }}
              type="dropdown"
              disableClearable
              options={optionsTypes ?? []}
              getOptionLabel={(option) => option?.label}
              value={
                (optionsTypes?.length > 0 &&
                  ingredient?.data?.type &&
                  optionsTypes?.find(
                    (c) => c.value === ingredient?.data?.type,
                  )) ||
                null
              }
              onChange={(e, newValue) => {
                onChange({
                  target: {
                    name: "type",
                    value: newValue?.value,
                  },
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Type"
                  required
                  error={validator.current.message(
                    "Type",
                    ingredient?.data?.type,
                    "required",
                  )}
                  helperText={validator.current.message(
                    "Type",
                    ingredient?.data?.type,
                    "required",
                  )}
                />
              )}
            />
          </Stack>

          {/* Status Toggle */}
          <Stack direction="row" justifyContent="space-between" width="50%">
            <Typography variant="subtitle1" fontWeight={600}>
              Status
            </Typography>
            <Switch
              name="status"
              checked={ingredient?.data?.status === 1}
              value={ingredient?.data?.status}
              onChange={(e) =>
                onChange({
                  target: {
                    value: e.target.checked ? 1 : 0,
                    name: "status",
                  },
                })
              }
            />
          </Stack>

          {/* Tags Section */}
          <Stack direction="row" alignItems="center">
            <Typography variant="subtitle2" fontWeight={600}>
              Category Tags
            </Typography>
            <Tooltip
              placement="bottom"
              arrow
              title="Must add one category tag, this tag is only used to filter category."
            >
              <InfoIcon sx={{ height: "13px", color: "#485364" }} />
            </Tooltip>
          </Stack>

          <Stack gap="8px">
            <InputControl
              value={ingredient?.data?.tags}
              type="dropdown"
              name="tags"
              label="Category Tags"
              multiple
              className="tags-input"
              isOptionEqualToValue={(option, value) => option === value}
              onChange={(event, newValue) => {
                if (typeof newValue === "string") {
                  handleProductInputChange({
                    target: {
                      name: "tags",
                      value: newValue,
                    },
                  });
                } else if (newValue && newValue.inputValue) {
                  handleProductInputChange({
                    target: {
                      name: "tags",
                      value: newValue.inputValue,
                    },
                  });
                } else {
                  handleProductInputChange({
                    target: {
                      name: "tags",
                      value: newValue,
                    },
                  });
                }
              }}
              options={optionsTags ?? []}
              filterOptions={(options, params) => {
                const filtered = filter(options, params);
                const { inputValue } = params;
                const isExisting = options.some(
                  (option) => inputValue === option,
                );
                if (inputValue !== "" && !isExisting) {
                  filtered.push(inputValue);
                }
                return filtered;
              }}
              selectOnFocus
              clearOnBlur
              handleHomeEndKeys
              id="free-solo-with-text-demo"
              renderTags={() => null}
              getOptionLabel={(option) => {
                if (typeof option === "string") return option;
                if (option.inputValue) return option.inputValue;
                return option;
              }}
              renderOption={(props, option) => <li {...props}>{option}</li>}
              freeSolo
              renderInput={(params) => <TextField {...params} label="Tags" />}
            />

            <Stack
              direction="row"
              alignItems="center"
              flexWrap="wrap"
              gap="12px"
              mt={1}
            >
              {ingredient?.data?.tags?.map((data, index) => (
                <Chip
                  key={index}
                  variant="outlined"
                  color="gray"
                  label={data}
                  deleteIcon={<CloseIcon />}
                  onDelete={() => onDeleteTagSelect(data)}
                  sx={{
                    width: "115px",
                    "& .MuiChip-deleteIcon": {
                      color: "#CCCCCC",
                    },
                  }}
                />
              ))}
            </Stack>
          </Stack>
        </Stack>
      </Grid>
    </Grid>
  );
}

export default BasicDetailsSection;
